import { Injectable } from '@angular/core'
import { PermissionService } from '@ftr/api-permissions'
import { CourtSystem } from '@ftr/contracts/api/court-system'
import { EntityType } from '@ftr/contracts/api/permissions'
import {
  PERMISSIONS,
  PERMISSIONS_BY_COURT_SYSTEM_FEATURE,
  UserGroup,
  UserGroupPermissionId,
  UserPermission,
  UserPermissionCategory,
} from '@ftr/contracts/api/user-group'
import { AudioPlaybackFeature } from '@ftr/contracts/read/audio-playback-feature'
import { FeatureName } from '@ftr/contracts/type/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, MapUtils, mapData, tapData } from '@ftr/foundation'
import {
  AudioPlaybackFeatureService,
  CourtSystemService,
  EnabledCourtSystemFeaturesService,
} from '@ftr/ui-court-system'
import { LaunchDarklyService, WebFeatureFlags } from '@ftr/ui-feature-flags'
import { BehaviorSubject, Observable, debounceTime, map, switchMap, takeWhile } from 'rxjs'
import { CurrentStage, UserGroupState } from '../util'
import { UserGroupService } from './user-group.service'

/**
 * The amount of time we wait for updates before updating the UI.
 *
 * When Selecting all, these come in as individual events.
 * This is to stop the ui trying to repaint 100 times
 *
 * Increasing this number also affects how long it takes for the state to reset when
 * moving to the "Add User Group" page, which can cause the last edited group name to appear.
 */
const USER_GROUP_STATE_DEBOUNCE = 10

export interface UserPermissionRestricted extends UserPermission {
  restrictedByLocation: boolean
  restrictedByDepartment: boolean
}

/**
 * This service keeps the state of a new user group shared between components
 */
@Injectable()
export class UserGroupStateService {
  private readonly state = new BehaviorSubject<UserGroupState>(new UserGroupState())

  constructor(
    private readonly courtSystemService: CourtSystemService,
    private readonly userGroupService: UserGroupService,
    private readonly permissionService: PermissionService,
    private readonly enabledCourtSystemFeaturesService: EnabledCourtSystemFeaturesService,
    private readonly audioPlaybackFeatureService: AudioPlaybackFeatureService,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {}

  private getPermissionsGroupedByCategory(): Map<UserPermissionCategory, UserPermission[]> {
    return MapUtils.buildGroupedFromValues(PERMISSIONS, permission => permission.category)
  }

  /**
   * Filters permissions based on enabled features
   */
  private getFilteredPermissionsGroupedByCategory(
    enabledFeatureMap: Map<FeatureName, boolean>,
    audioPlaybackFeature: AudioPlaybackFeature,
    flags: WebFeatureFlags,
  ): Map<UserPermissionCategory, UserPermission[]> {
    const unfiltered = this.getPermissionsGroupedByCategory()

    return MapUtils.mapValues(unfiltered, permissions =>
      permissions.filter(permission =>
        this.isVisible(permission.permissionId, enabledFeatureMap, audioPlaybackFeature, flags),
      ),
    )
  }

  private isVisible(
    permissionId: UserGroupPermissionId,
    enabledFeatureMap: Map<FeatureName, boolean>,
    audioPlaybackFeature: AudioPlaybackFeature,
    flags: WebFeatureFlags,
  ): boolean {
    if (
      [UserGroupPermissionId.CaseInformation, UserGroupPermissionId.PlaybackHearings].includes(permissionId) &&
      !flags['annotations-first-release']
    ) {
      return false
    }

    if (permissionId === UserGroupPermissionId.ManagePastSessions && !flags['release-modifying-sessions']) {
      return false
    }

    // Remove once AEC is passed internal validation
    if (permissionId === UserGroupPermissionId.AdvancedRealtimeRecorderConfiguration) {
      return false
    }

    if (
      [UserGroupPermissionId.ReadStt, UserGroupPermissionId.RequestStt].includes(permissionId) &&
      !audioPlaybackFeature.configuration?.autoscriptEnabled
    ) {
      return false
    }

    const featuresGuardingThis = PERMISSIONS_BY_COURT_SYSTEM_FEATURE[permissionId] ?? []

    if (featuresGuardingThis.length === 0) {
      return true
    }

    if ([UserGroupPermissionId.ShareRecordings, UserGroupPermissionId.DownloadBundles].includes(permissionId)) {
      // all features must be enabled
      return featuresGuardingThis.every(f => enabledFeatureMap.get(f))
    }

    // at least one feature must be enabled
    return featuresGuardingThis.some(f => enabledFeatureMap.get(f))
  }

  getCurrentUserGroupPermissionsCategorized(
    courtSystemId: Uuid,
  ): ApiResult<Map<UserPermissionCategory, UserPermissionRestricted[]>> {
    return ApiResult.combine([
      this.getVisiblePermissionsGroupedByCategory(courtSystemId),
      this.permissionService.getEnabledRestrictedPermissionsByPermission(courtSystemId),
      ApiResult.from(this.launchDarklyService.observeFlagChanges<boolean>('annotations-first-release')),
    ]).pipe(
      mapData(([categorizedPermissions, enabledRestrictedPermissions, annotationsFirstReleaseFlag]) =>
        MapUtils.mapValues(categorizedPermissions, permissions =>
          permissions
            .filter(
              h =>
                (h.permissionId !== UserGroupPermissionId.CaseInformation &&
                  h.permissionId !== UserGroupPermissionId.PlaybackHearings) ||
                annotationsFirstReleaseFlag,
            )
            .map(p => this.addRestrictions(p, enabledRestrictedPermissions)),
        ),
      ),
    )
  }

  /**
   * Retrieves the user group state.
   * Populates the user group data from the server if needed.
   */
  observeUserGroupState(courtSystemId: Uuid, userGroupId: Uuid | undefined): ApiResult<UserGroupState> {
    return this.populateUserGroupState(courtSystemId, userGroupId).pipe(
      switchMap(_ =>
        ApiResult.from(
          this.state.pipe(
            takeWhile(
              x => x.courtSystemId === courtSystemId && (userGroupId === undefined || x.userGroupId === userGroupId),
            ),
            debounceTime(USER_GROUP_STATE_DEBOUNCE),
          ),
        ),
      ),
    )
  }

  /**
   * If we already have retrieved the state from the API for this user.
   */
  private populateUserGroupState(courtSystemId: Uuid, userGroupId: Uuid | undefined): ApiResult<UserGroupState> {
    const currentState = this.state.getValue()
    if (currentState.courtSystemId === courtSystemId && currentState.userGroupId === userGroupId) {
      return ApiResult.success(currentState)
    }

    return this.fetchAndPopulateState(courtSystemId, userGroupId)
  }

  private fetchAndPopulateState(courtSystemId: Uuid, userGroupId: Uuid | undefined): ApiResult<UserGroupState> {
    return ApiResult.combine([
      this.courtSystemService.getCourtSystem(courtSystemId),
      this.getOptionalUserGroup(userGroupId),
      this.permissionService.getEnabledRestrictedPermissionsByPermission(courtSystemId),
    ]).pipe(
      mapData(([courtSystem, userGroup, enabledRestrictedPermissions]) =>
        this.generateState(enabledRestrictedPermissions, courtSystem, userGroup),
      ),
      tapData(state => this.state.next(state)),
    )
  }

  private generateState(
    enabledRestrictedPermissions: Map<UserGroupPermissionId, Set<EntityType>>,
    courtSystem: CourtSystem,
    userGroup: UserGroup | undefined,
  ): UserGroupState {
    const state = new UserGroupState(enabledRestrictedPermissions)
    state.courtSystemId = courtSystem.id
    state.courtSystemName = courtSystem.name

    if (userGroup) {
      state.userGroupId = userGroup.id
      state.userGroupName = userGroup.name || ''
      state.userGroupDescription = userGroup.description || ''
      state.populateFromUserGroupPermissions(userGroup.permissions)
    }

    return state
  }

  // User group is undefined if we are adding a new group
  private getOptionalUserGroup(userGroupId: Uuid | undefined): ApiResult<UserGroup | undefined> {
    if (userGroupId) {
      return this.userGroupService.get(userGroupId)
    }
    return ApiResult.success(undefined)
  }

  transformState(f: (s: UserGroupState) => void): void {
    const state = this.state.getValue()
    f(state)
    this.state.next(state)
  }

  private getVisiblePermissionsGroupedByCategory(
    courtSystemId: Uuid,
  ): ApiResult<Map<UserPermissionCategory, UserPermission[]>> {
    return ApiResult.combine([
      this.enabledCourtSystemFeaturesService.getEnabledFeatureMap(courtSystemId),
      this.audioPlaybackFeatureService.getByCourtSystem(courtSystemId),
      ApiResult.from(this.launchDarklyService.getFlagMap()),
    ]).pipe(
      mapData(([enabledFeatureMap, audioPlaybackFeature, flags]) => {
        const categories = this.getFilteredPermissionsGroupedByCategory(enabledFeatureMap, audioPlaybackFeature, flags)

        // Filter out empty categories
        return MapUtils.filterValues(categories, permissions => permissions.length > 0)
      }),
    )
  }

  private addRestrictions(
    permission: UserPermission,
    enabledRestrictedPermissions: Map<UserGroupPermissionId, Set<EntityType>>,
  ): UserPermissionRestricted {
    const entities = enabledRestrictedPermissions.get(permission.permissionId) || new Set()
    return {
      ...permission,
      restrictedByLocation: entities.has('location'),
      restrictedByDepartment: entities.has('department'),
    }
  }

  observeRestrictions(
    permissionId: UserGroupPermissionId,
    type: EntityType,
    stage: CurrentStage,
  ): Observable<Set<Uuid>> {
    return this.state.pipe(map(state => state.getEntityIds(permissionId, type, stage)))
  }

  addRestriction(permissionId: UserGroupPermissionId, entityType: EntityType, id: string): void {
    this.transformState(s => s.addRestriction(permissionId, entityType, id))
  }

  removeRestriction(permissionId: UserGroupPermissionId, entityType: EntityType, id: string): void {
    this.transformState(s => s.removeRestriction(permissionId, entityType, id))
  }

  removeAllRestrictions(permissionId: UserGroupPermissionId, entityType: EntityType): void {
    this.transformState(s => s.removeAllRestrictions(permissionId, entityType))
  }

  reset(): void {
    this.state.next(new UserGroupState())
  }

  commitRestrictions(permissionType: UserGroupPermissionId): void {
    this.transformState(s => s.commitRestrictions(permissionType))
  }

  resetRestrictions(): void {
    this.transformState(s => s.resetRestrictions())
  }

  setUnlinkedAccess(permissionId: UserGroupPermissionId, entityType: EntityType, selected: boolean): void {
    this.transformState(s => s.setUnlinkedAccess(permissionId, entityType, selected))
  }
}
